/**
 * Common NavigationRail component used by other modules as a reusable component that returns the Navigational Rail UI.
 *
 * @module views/components/NavigationRail
 * @memberof -Common
 */
import './ImageNavigation.scss';

import React, { useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Text from '@ulta/core/components/Text/Text';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { hasItems } from '@ulta/core/utils/array/array';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';

import NavigationRailGroup from '../NavigationRailGroup/NavigationRailGroup';
import NavigationRailHorizontal from '../NavigationRailHorizontal/NavigationRailHorizontal';
/**
 * Represents a NavigationRail component
 *
 * @method
 * @param {NavigationRailProps} props - React properties passed from composition
 * @returns nNavigationRail
 */
const ImageNavigation = function( props ){
  const {
    title,
    items,
    fullBleedBackgroundColor,
    mobileRailOption,
    railOption = NAVIGATION_RAIL_OPTION_GROUP,
    root,
    rootMargin,
    threshold
  } = props;
  const { breakpoint } = useDeviceInflection();
  const navRef = useRef( null );

  useIntersectionObserver( navRef, {
    root,
    rootMargin,
    threshold
  }, handleIntersection( props ) );

  if( !hasItems( items ) ){
    return null;
  }

  const responsiveRailOption = breakpoint.CURRENT_BREAKPOINT === 'SM' && mobileRailOption ? mobileRailOption : railOption;
  const shouldDisplayGroup = responsiveRailOption === NAVIGATION_RAIL_OPTION_GROUP;
  const titleStyle = shouldDisplayGroup ? 'title-xlg' : 'title-lg';
  const titleAlign = shouldDisplayGroup ? 'center' : 'left';
  const layoutStyle = shouldDisplayGroup ? 'Group' : 'Rail';

  return (
    <div className={ classNames( 'ImageNavigation', `ImageNavigation--${layoutStyle}`, {
      [`ImageNavigation--background`]: fullBleedBackgroundColor
    } ) }
    ref={ navRef }
    >
      <div className='ImageNavigation__grid'>
        <div className={ classNames( 'ImageNavigation__grid-container', {
          [`ImageNavigation--${fullBleedBackgroundColor}`]: fullBleedBackgroundColor
        } ) }
        >
          <div className='ImageNavigation__content'>
            <div className='ImageNavigation__header'>
              { title &&
              <Text
                textStyle={ titleStyle }
                textAlign={ titleAlign }
                htmlTag={ 'h2' }
              >
                { title }
              </Text>
              }
            </div>
            <div className='ImageNavigation__items'>
              { shouldDisplayGroup ? (
                <NavigationRailGroup { ...props } />
              ) : (
                <NavigationRailHorizontal { ...props } />
              ) }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * @const {string} NAVIGATION_RAIL_OPTION_RAIL - option for vertical rail view
 */
export const NAVIGATION_RAIL_OPTION_RAIL = 'image-rail';

/**
 * @const {string} NAVIGATION_RAIL_OPTION_GROUP - option for horizontal rail view
 */
export const NAVIGATION_RAIL_OPTION_GROUP = 'image-group';

/**
 * Property type definitions
 * @typedef ImageNavigationProps
 * @type {object}
 * @property {string} fullBleedBackgroundColor - Sets the background color.
 * @property {string} mobileRailOption - Sets display type for mobile devices.
 * @property {string} railOption - Sets display type.
 * @property {array} items - Sets the items to be displayed as NavigationCards.
 * @property {string} title - Sets the title.
 */
export const propTypes = {
  fullBleedBackgroundColor: PropTypes.string,
  mobileRailOption: PropTypes.string,
  railOption: PropTypes.string,
  items: PropTypes.array,
  title: PropTypes.string
};

ImageNavigation.propTypes = propTypes;

export default ImageNavigation;
